import React from "react";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Link } from "react-router-dom"
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Heart, Shield, Clock, Phone, Mail, MapPin, Stethoscope, Baby, Activity, Users,ArrowRight,Award } from "lucide-react";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
export default function PediatricSurgeryPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Pediatric Surgery");


  const subspecialties = [
    {
      title: "Neonatal Surgery",
      description: "Treatment for birth defects and congenital anomalies.",
      image: "/NeonatalSurgery.jpg",
    },
    {
      title: "Pediatric Urology",
      description: "Surgery for urinary tract and genital issues in children.",
      image: "/PediatricUrology.jpg",
    },
    {
      title: "Pediatric Gastrointestinal Surgery",
      description: "For intestinal and abdominal conditions.",
      image: "/PediatricGastrointestinalSurgery.jpg",
    },
    {
      title: "Laparoscopic Pediatric Surgery",
      description: "Minimally invasive procedures for children.",
      image: "/LaparoscopicPediatricSurgery.jpg",
    },
    {
      title: "Pediatric Trauma Care",
      description: "Immediate surgical care for injuries in children.",
      image: "/PediatricTraumaCare.jpg",
    },
  ];

  const treatments = [
    {
      title: "Congenital Anomaly Correction",
      description: "Surgical correction of birth defects and structural abnormalities.",
      icon: Baby,
    },
    {
      title: "Hernia & Hydrocele Repair",
      description: "Minimally invasive treatment for common pediatric conditions.",
      icon: Activity,
    },
    {
      title: "Appendectomy & Intestinal Surgery",
      description: "Emergency and routine gastrointestinal surgeries.",
      icon: Stethoscope,
    },
    {
      title: "Laparoscopic Abdominal Surgery",
      description: "Minimally invasive abdominal procedures for quick recovery.",
      icon: Shield,
    },
    {
      title: "Pediatric Urologic Procedures",
      description: "Surgical care for urinary and genital conditions.",
      icon: Heart,
    },
    {
      title: "Trauma & Burn Management",
      description: "Comprehensive care for pediatric injuries and burns.",
      icon: Users,
    },
  ];

  const whyChoose = [
    {
      title: "Dedicated Pediatric Surgical Team",
      description: "Specialized surgeons and anesthetists trained for children.",
      icon: Users,
    },
    {
      title: "Child-Friendly Infrastructure",
      description: "Pediatric ICU & recovery rooms designed for comfort.",
      icon: Heart,
    },
    {
      title: "Minimally Invasive Techniques",
      description: "Advanced laparoscopic procedures with faster recovery.",
      icon: Shield,
    },
    {
      title: "24x7 Pediatric Emergency Support",
      description: "Round-the-clock care for surgical emergencies.",
      icon: Clock,
    },
    {
      title: "Convenient Location",
      description: "Easily accessible from Miyapur & Bachupally.",
      icon: MapPin,
    },
  ];

  const articles = [
    {
      title: "When Does a Child Need Surgery?",
      description: "Understanding when surgery is essential for children.",
    },
    {
      title: "Common Congenital Anomalies & Treatment",
      description: "Birth defects and available surgical options.",
    },
    {
      title: "Minimally Invasive Surgery in Children",
      description: "Benefits of laparoscopic pediatric procedures.",
    },
    {
      title: "How to Prepare Your Child for Surgery",
      description: "A guide for parents before pediatric surgery.",
    },
  ];

  const faqs = [
    {
      question: "Are pediatric surgeries safe?",
      answer:
        "Yes, our pediatric anesthetists and surgeons use child-specific equipment and protocols to ensure maximum safety.",
    },
    {
      question: "What age group does pediatric surgery cover?",
      answer:
        "From newborns to adolescents up to 18 years, covering all pediatric age ranges with specialized care.",
    },
    {
      question: "Do you have child-specific ICUs?",
      answer:
        "Yes, we have a dedicated pediatric ICU staffed with trained pediatric critical care professionals.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Pediatric Surgery Department",
    "url": "https://mamshospitals.com/pediatric-surgery",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/PediatricSurgery.jpg",
    "description": "MAMS Hospitals provides advanced pediatric surgery treatment in Hyderabad including neonatal surgery, pediatric urology, laparoscopic surgery, congenital anomaly correction, and trauma care for children.",
    "medicalSpecialty": "Pediatric Surgery",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Neonatal Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Congenital Anomaly Correction"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pediatric Laparoscopic Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pediatric Urology Procedures"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pediatric Trauma Care"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Are pediatric surgeries safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, pediatric surgeries at MAMS Hospitals are performed using child-specific equipment, pediatric anesthetists, and specialized safety protocols."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide neonatal surgery services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides specialized neonatal surgery and congenital anomaly correction for newborns and infants."
        }
      },
      {
        "@type": "Question",
        "name": "Do you have pediatric ICU facilities?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals has a dedicated pediatric ICU and child-friendly recovery facilities."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pediatric Surgery Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/pediatric-surgery",
    "description": "Best pediatric surgery hospital in Hyderabad offering neonatal surgery, congenital anomaly correction, pediatric urology, laparoscopic surgery, and pediatric trauma care.",
    "keywords": [
      "Pediatric Surgery Hospital Hyderabad",
      "Best Pediatric Surgeon Hyderabad",
      "Neonatal Surgery Hyderabad",
      "Pediatric Urology Hyderabad",
      "Congenital Anomaly Surgery Hyderabad",
      "Pediatric Trauma Care Hyderabad",
      "Pediatric Surgery Hospital Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
  title="Best Pediatric Surgery Hospital in Hyderabad | Child Surgery Specialists | MAMS Hospitals"
  description="MAMS Hospitals offers advanced pediatric surgery in Hyderabad including neonatal surgery, congenital anomaly correction, pediatric urology, laparoscopic surgery, and trauma care for children."
  keywords="pediatric surgery hospital Hyderabad,
  best pediatric surgeon Hyderabad,
  neonatal surgery Hyderabad,
  pediatric urology Hyderabad,
  child surgery Hyderabad,
  congenital anomaly surgery Hyderabad,
  pediatric laparoscopic surgery Hyderabad,
  pediatric trauma care Hyderabad,
  pediatric ICU Hyderabad,
  child specialist surgeon Hyderabad,
  pediatric surgery hospital Bachupally,
  newborn surgery Hyderabad,
  MAMS Hospitals Pediatric Surgery"
  schema={schema}
  />
    <div className="flex flex-col min-h-screen">

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl flex flex-col md:flex-row items-center gap-8">

          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Delicate Care for Little Heroes
            </h1>

            <p className="text-lg text-gray-700 mb-6">
              MAMS Hospitals provides specialized surgical care for infants,
              children, and adolescents with precision, compassion, and expertise.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
          </div>

          <div className="flex-1 relative">
            <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/PediatricSurgery.jpg"
                alt="Pediatric Surgery"
                className="w-full h-full object-cover"
              canonicalUrl="https://mamshospitals.com/departments/pediatric-surgery"
      />
            </div>

            <div className="absolute -bottom-4 right-2 sm:-right-4 bg-white p-4 sm:p-6 rounded-xl shadow-lg border-2 border-primary/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">98%</div>
                <div className="text-sm text-gray-600">Success Rate</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* SUB-SPECIALTIES */}
      <section className="py-16 md:py-20 bg-white px-0">
        <div className="container mx-auto max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary">
              Sub-Specialties in Pediatric Surgery
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive surgical care across a wide range of pediatric specialties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, i) => (
              <Card key={i} className="overflow-hidden hover:shadow-lg transition">
                <div className="h-48">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-primary">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* TREATMENTS */}
      <section className="py-16 md:py-20 bg-slate-50 px-0">
        <div className="container mx-auto max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary">
              Treatments & Procedures
            </h2>
            <p className="text-gray-600">Advanced pediatric surgical procedures.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((t, i) => {
              const Icon = t.icon;
              return (
                <Card key={i} className="hover:shadow-lg transition">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>

                    <h3 className="text-xl font-bold">{t.title}</h3>
                    <p className="text-gray-600">{t.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white px-0">
        <div className="container mx-auto max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary">
              Why Choose MAMS Hospitals for Pediatric Surgery
            </h2>
            <p className="text-gray-600">
              World-class pediatric surgical care with compassion and expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="border-2 hover:border-primary/50 transition">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>

                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* SPECIALIST */}
                  {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-slate-50 px-0">
        <div className="container mx-auto max-w-4xl">

          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="font-semibold text-gray-900 hover:text-primary">
                  {faq.question}
                </AccordionTrigger>

                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
